import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, colones } from "@/lib/api";
import { CreditCard, Upload, CheckCircle2, XCircle, Clock, Receipt, Eye, AlertCircle, Package, Calendar as CalendarIcon, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PLANES, FRECUENCIAS_PAGO } from "@/pages/Landing";
import { computeSuscripcion } from "@/lib/subscription";

const METODOS = [
  { key: "sinpe", label: "SINPE Móvil" },
  { key: "transferencia", label: "Transferencia bancaria" },
];
const PERIODOS = [
  { key: "mensual", label: "Mensual" },
  { key: "trimestral", label: "Trimestral" },
  { key: "semestral", label: "Semestral" },
  { key: "anual", label: "Anual" },
];

export default function PagosCliente() {
  const { user } = useAuth();
  const [pagos, setPagos] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [empleadosCount, setEmpleadosCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [ver, setVer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, eRes, emRes] = await Promise.all([
        api.get("/pagos"),
        api.get("/empresa").catch(() => ({ data: null })),
        api.get("/empleados").catch(() => ({ data: [] })),
      ]);
      setPagos(pRes.data || []);
      setEmpresa(eRes.data || null);
      const activos = (emRes.data || []).filter(e => e.estado === "Activo").length;
      setEmpleadosCount(activos);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron cargar los pagos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Info de la suscripción actual del cliente
  const planInfo = useMemo(() => computeSuscripcion(empresa, empleadosCount), [empresa, empleadosCount]);

  const resumen = useMemo(() => {
    let aprobado = 0, pendiente = 0, rechazado = 0;
    for (const p of pagos) {
      if (p.estado === "aprobado") aprobado += Number(p.monto || 0);
      else if (p.estado === "pendiente") pendiente += Number(p.monto || 0);
      else if (p.estado === "rechazado") rechazado += Number(p.monto || 0);
    }
    return { aprobado, pendiente, rechazado, total: pagos.length };
  }, [pagos]);

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <CreditCard size={26} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Suscripción</h1>
            <p className="text-sm text-secondary-c">Registrá tus pagos adjuntando el comprobante de SINPE o transferencia. El super administrador revisa y aprueba.</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="np-btn np-btn-primary" data-testid="pago-nuevo-btn">
          <Upload size={16} /> Registrar pago
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={<Receipt size={16} />} label="Total pagos" value={resumen.total} tone="neutral" />
        <StatCard icon={<CheckCircle2 size={16} />} label="Aprobado" value={colones(resumen.aprobado)} tone="success" />
        <StatCard icon={<Clock size={16} />} label="Pendiente" value={colones(resumen.pendiente)} tone="warning" />
        <StatCard icon={<XCircle size={16} />} label="Rechazado" value={colones(resumen.rechazado)} tone="error" />
      </div>

      {planInfo && (
        <div className="np-card mb-6 p-5 border-l-4 border-primary-c" data-testid="pagos-suscripcion">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className="w-11 h-11 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
                <Package size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Suscripción actual</div>
                <div className="font-display text-2xl font-semibold text-primary-c leading-tight">Plan {planInfo.plan.nombre}</div>
                <div className="text-xs text-secondary-c mt-0.5">
                  {planInfo.plan.rango} · Frecuencia {planInfo.frecuencia.label}
                  {planInfo.frecuencia.descuento > 0 ? ` (−${Math.round(planInfo.frecuencia.descuento * 100)}%)` : ""}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div className="p-2.5 rounded-lg bg-[#FAFAF7] border border-c">
                <div className="text-secondary-c uppercase tracking-wider text-[10px] font-semibold flex items-center gap-1"><UsersIcon size={11} /> Colaboradores</div>
                <div className="font-semibold text-base mt-0.5">{planInfo.nColabs}</div>
              </div>
              <div className="p-2.5 rounded-lg bg-[#FAFAF7] border border-c">
                <div className="text-secondary-c uppercase tracking-wider text-[10px] font-semibold">Total del ciclo</div>
                <div className="font-semibold text-base mt-0.5 text-primary-c">{colones(planInfo.total)}</div>
                <div className="text-[10px] text-secondary-c">IVA incluido</div>
              </div>
              <div className="p-2.5 rounded-lg bg-[#FDF4E1] border border-[#E7D69B] col-span-2 md:col-span-1">
                <div className="text-[#8A6D18] uppercase tracking-wider text-[10px] font-semibold flex items-center gap-1"><CalendarIcon size={11} /> Próximo pago</div>
                <div className="font-semibold text-sm mt-0.5">{fmtFecha(planInfo.proximoPagoISO)}</div>
                <div className="text-[10px] text-[#8A6D18]">
                  {planInfo.diasRest <= 0 ? "Vencido — pagá ya" : `en ${planInfo.diasRest} día${planInfo.diasRest === 1 ? "" : "s"}`}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-secondary-c">
            Cobro el día 25 · tenés 8 días para acreditar el pago vía SINPE Móvil o transferencia. Adjuntá el comprobante con "Registrar pago" arriba.
          </div>
        </div>
      )}

      {loading ? (
        <div className="np-card p-10 text-center text-sm text-secondary-c">Cargando…</div>
      ) : pagos.length === 0 ? (
        <div className="np-card p-12 text-center">
          <Receipt size={40} className="mx-auto text-secondary-c opacity-40 mb-3" />
          <div className="font-medium">Aún no has registrado ningún pago</div>
          <div className="text-xs text-secondary-c mt-1">Hacé clic en "Registrar pago" para adjuntar tu primer comprobante.</div>
        </div>
      ) : (
        <div className="np-card p-0 np-scroll">
          <table className="np-table text-sm">
            <thead><tr>
              <th>Fecha</th><th>Método</th><th>Período</th>
              <th className="text-right">Monto</th>
              <th>Notas</th><th>Estado</th><th>Comprobante</th>
            </tr></thead>
            <tbody>
              {pagos.map(p => (
                <tr key={p.id} data-testid={`mi-pago-${p.id}`}>
                  <td>{fmtFecha(p.fecha_pago)}</td>
                  <td className="capitalize">{p.metodo}</td>
                  <td className="capitalize">{p.periodo}</td>
                  <td className="text-right font-semibold">{colones(p.monto)}</td>
                  <td className="text-xs text-secondary-c max-w-[220px] truncate" title={p.notas}>{p.notas || "—"}</td>
                  <td>
                    {p.estado === "pendiente" && <span className="np-badge np-badge-warning text-[10px]">Pendiente</span>}
                    {p.estado === "aprobado" && <span className="np-badge np-badge-success text-[10px]">Aprobado</span>}
                    {p.estado === "rechazado" && (
                      <span className="np-badge np-badge-error text-[10px]" title={p.rechazo_motivo || ""}>Rechazado</span>
                    )}
                  </td>
                  <td>
                    {p.comprobante_base64 && (
                      <button onClick={() => setVer(p)} className="text-primary-c text-xs underline" data-testid={`mi-pago-ver-${p.id}`}>
                        <Eye size={12} className="inline" /> Ver
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <NuevoPagoForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {ver && <ComprobanteViewer pago={ver} onClose={() => setVer(null)} />}
    </Layout>
  );
}

function StatCard({ icon, label, value, tone }) {
  const bg = tone === "success" ? "bg-[#E6EFEA] text-primary-color"
    : tone === "warning" ? "bg-[#FDF4E1] text-[#8A6D18]"
    : tone === "error" ? "bg-[#FDEDED] text-error"
    : "bg-[#F0F0EA] text-primary-c";
  return (
    <div className="np-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-secondary-c font-semibold mb-2">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${bg}`}>{icon}</span>
        {label}
      </div>
      <div className="text-lg font-display font-semibold">{value}</div>
    </div>
  );
}

function NuevoPagoForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    metodo: "sinpe", periodo: "mensual", monto: "",
    fecha_pago: new Date().toISOString().slice(0, 10),
    notas: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Límite 5MB para evitar payloads gigantes en MongoDB
    if (f.size > 5 * 1024 * 1024) {
      toast.error("El archivo no debe superar 5 MB");
      return;
    }
    if (!/\.(png|jpe?g|webp|pdf)$/i.test(f.name)) {
      toast.error("Solo se aceptan imágenes (PNG/JPG/WEBP) o PDF");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!preview) { toast.error("Adjuntá el comprobante"); return; }
    if (!form.monto || Number(form.monto) <= 0) { toast.error("Monto inválido"); return; }
    setSaving(true);
    try {
      await api.post("/pagos", {
        ...form,
        monto: Number(form.monto),
        comprobante_base64: preview,
        comprobante_nombre: file?.name || "",
      });
      toast.success("Pago registrado. Queda pendiente de validación.");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el pago");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <form className="np-card p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} onSubmit={submit} data-testid="nuevo-pago-form">
        <h3 className="font-display text-xl font-semibold mb-1">Registrar pago</h3>
        <p className="text-xs text-secondary-c mb-4">Adjuntá el comprobante (SINPE o transferencia). Se validará por el super administrador.</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="np-label">Método</label>
            <select className="np-input" value={form.metodo} onChange={e => setForm({ ...form, metodo: e.target.value })} data-testid="pago-metodo">
              {METODOS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="np-label">Período</label>
            <select className="np-input" value={form.periodo} onChange={e => setForm({ ...form, periodo: e.target.value })} data-testid="pago-periodo">
              {PERIODOS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="np-label">Fecha del pago</label>
            <input type="date" className="np-input" value={form.fecha_pago} onChange={e => setForm({ ...form, fecha_pago: e.target.value })} required data-testid="pago-fecha" />
          </div>
          <div>
            <label className="np-label">Monto (₡)</label>
            <input type="number" min="1" step="0.01" className="np-input" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} required placeholder="0.00" data-testid="pago-monto" />
          </div>
          <div className="col-span-2">
            <label className="np-label">Notas (opcional)</label>
            <textarea className="np-input min-h-[60px]" value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} placeholder="Ej: cubre enero-marzo 2026, referencia SINPE 1234…" data-testid="pago-notas" />
          </div>
          <div className="col-span-2">
            <label className="np-label">Comprobante <span className="text-secondary-c">(PNG, JPG, WEBP o PDF · máx 5MB)</span></label>
            <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={handleFile} className="np-input" required data-testid="pago-file" />
            {preview && !preview.startsWith("data:application/pdf") && (
              <img src={preview} alt="preview" className="mt-2 max-h-48 rounded-lg border border-c object-contain" />
            )}
            {preview && preview.startsWith("data:application/pdf") && (
              <div className="mt-2 p-2 bg-[#FAFAF7] border border-c rounded-lg text-xs flex items-center gap-2">
                <Receipt size={14} /> PDF adjunto: {file?.name}
              </div>
            )}
          </div>
        </div>

        <div className="p-3 rounded-xl bg-[#FDF4E1] border border-[#E7D69B] text-xs text-[#8A6D18] flex items-start gap-2 mt-4">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>El pago queda en estado <strong>Pendiente</strong> hasta que se valide desde el panel de super administrador. Si el pago se rechaza, verás el motivo aquí mismo.</span>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="np-btn np-btn-ghost" data-testid="pago-cancel">Cancelar</button>
          <button type="submit" disabled={saving} className="np-btn np-btn-primary disabled:opacity-50" data-testid="pago-submit">
            {saving ? "Guardando…" : "Registrar pago"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ComprobanteViewer({ pago, onClose }) {
  const isPDF = (pago.comprobante_nombre || "").toLowerCase().endsWith(".pdf") || (pago.comprobante_base64 || "").startsWith("data:application/pdf");
  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-4 max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium text-sm">Comprobante · {pago.comprobante_nombre || "sin nombre"}</div>
          <button onClick={onClose} className="np-btn np-btn-ghost p-1"><XCircle size={18} /></button>
        </div>
        {isPDF ? (
          <iframe title="comprobante" src={pago.comprobante_base64} className="w-full h-[70vh] rounded-lg border border-c" />
        ) : (
          <img src={pago.comprobante_base64} alt="Comprobante" className="w-full max-h-[70vh] object-contain rounded-lg" />
        )}
      </div>
    </div>
  );
}
